import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import EditIcon from '@atlaskit/icon/core/edit';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import Tag from '@atlaskit/tag/removable-tag';
import { token } from '@atlaskit/tokens';

import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { AttachmentActionMetadata } from '@confluence/forge-ui/entry-points/AttachmentActionMetadata.ts';
import type { ForgeUIAttachmentActionExtensionType } from '@confluence/forge-ui/entry-points/attachmentAction.ts';
import { AttachmentIcon } from '@confluence/icons/entry-points/AttachmentIcon.ts';
import { EditLabelDialogLoader } from '@confluence/labels/entry-points/EditLabelDialogLoader.ts';
import { buildLabelUrl } from '@confluence/macro-utils/entry-points/labelUrl.ts';
import { ProfileCardWrapper } from '@confluence/profile/entry-points/ProfileCardWrapper.ts';
import { ProfileLink } from '@confluence/profile/entry-points/ProfileLinkComponent.ts';

import { AttachmentsMacroDeleteDialogLoader } from '../dialogs/AttachmentsMacroDeleteDialogLoader.tsx';
import { AttachmentsMacroEditPropertiesDialogLoader } from '../dialogs/AttachmentsMacroEditPropertiesDialogLoader.tsx';
import type { AttachmentItem } from '../attachmentsMacroTypes.ts';
import { i18n } from '../attachmentsMacroi18n.ts';
import { useAttachmentVersions } from '../hooks/useAttachmentVersionsQuery.ts';
import { toPreviewAttachmentId } from '../hooks/useLegacyAttachmentPreview.ts';
import { resolveDownloadUrl } from '../downloadUrl.ts';

import {
	AttachmentMacroForgeActions,
	type AttachmentMacroActionContext,
} from './AttachmentMacroForgeActions.tsx';
import { AttachmentWebItems } from './AttachmentWebItems.tsx';
import { SmartDate } from './SmartDate.tsx';
import { VersionHistory } from './VersionHistory.tsx';

const styles = cssMap({
	// Styled to match ADS Button appearance="default" spacing="compact" visually,
	// but renders as a real <a> so the legacy jQuery delegation catches clicks.
	// (The Connect web-item actions use the ADS LinkButton component directly for the same look.)
	previewActionLink: {
		display: 'inline-flex',
		alignItems: 'center',
		boxSizing: 'border-box',
		paddingBlock: token('space.025'),
		paddingInline: token('space.150'),
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text.subtle'),
		fontWeight: token('font.weight.medium'),
		font: token('font.body'),
		textDecoration: 'none',
		cursor: 'pointer',
		'&:visited': {
			color: token('color.text.subtle'),
		},
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			color: token('color.text.subtle'),
			textDecoration: 'none',
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	table: {
		width: '100%',
		borderCollapse: 'collapse',
		tableLayout: 'auto',
	},
	headerRow: {
		borderBlockStartWidth: token('border.width'),
		borderBlockStartStyle: 'solid',
		borderBlockStartColor: token('color.border'),
		borderBlockEndWidth: token('border.width'),
		borderBlockEndStyle: 'solid',
		borderBlockEndColor: token('color.border'),
	},
	// Wide enough to push "Modified" right, matching the legacy layout.
	firstHeaderCell: {
		textAlign: 'start',
		width: '77%',
		paddingBlock: token('space.100'),
		paddingInlineStart: token('space.0'),
		paddingInlineEnd: token('space.100'),
		verticalAlign: 'middle',
	},
	bodyCell: {
		width: '77%',
		borderBlockEndWidth: token('border.width'),
		borderBlockEndStyle: 'solid',
		borderBlockEndColor: token('color.border'),
		paddingBlock: token('space.150'),
		paddingInline: token('space.100'),
		verticalAlign: 'middle',
	},
	modifiedHeaderCell: {
		textAlign: 'start',
		paddingBlock: token('space.100'),
		paddingInlineStart: token('space.300'),
		paddingInlineEnd: token('space.100'),
		verticalAlign: 'middle',
	},
	modifiedBodyCell: {
		borderBlockEndWidth: token('border.width'),
		borderBlockEndStyle: 'solid',
		borderBlockEndColor: token('color.border'),
		paddingBlock: token('space.150'),
		paddingInlineStart: token('space.300'),
		paddingInlineEnd: token('space.100'),
		verticalAlign: 'middle',
	},
	expandedCell: {
		borderBlockEndWidth: token('border.width'),
		borderBlockEndStyle: 'solid',
		borderBlockEndColor: token('color.border'),
		paddingBlock: token('space.0'),
		paddingInline: token('space.0'),
	},
	fileLink: {
		color: token('color.link'),
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'underline',
		},
	},
	expandedDetailsWrapper: {
		paddingBlockStart: token('space.150'),
		paddingBlockEnd: token('space.200'),
		paddingInlineStart: token('space.500'),
		paddingInlineEnd: token('space.300'),
	},
	labelsContentIndent: {
		paddingInlineStart: token('space.050'),
	},
	// Thumbnail preview or fallback card; both open the legacy overlay on click.
	previewContainer: {
		overflow: 'hidden',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		backgroundColor: token('elevation.surface.sunken'),
		width: 'fit-content',
		maxWidth: '100%',
		cursor: 'pointer',
	},
	previewImage: {
		display: 'block',
		maxWidth: '100%',
		maxHeight: '320px',
		width: 'auto',
		height: 'auto',
		objectFit: 'contain',
	},
	previewFallback: {
		paddingBlockStart: token('space.150'),
		paddingBlockEnd: token('space.150'),
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		minWidth: '320px',
	},
	expandButton: {
		cursor: 'pointer',
		padding: token('space.050'),
		display: 'flex',
		alignItems: 'center',
		backgroundColor: token('color.background.neutral.subtle'),
	},
	// Invisible spacer that matches the expand button's width to maintain alignment.
	expandButtonSpacer: {
		width: '24px',
		flexShrink: 0,
	},
});

type AttachmentsTableProps = {
	attachments: AttachmentItem[];
	dspBlocksDownloads: boolean;
	showVersionHistory: boolean;
	/** Macro `preview` param — show inline thumbnail in expanded row. */
	showPreview: boolean;
	/** Numeric content ID (not the GraphQL ARI). */
	contentId?: string;
	/** Space key — needed by the label editor. */
	spaceKey?: string;
	/** Called after delete/edit so the table refetches. */
	onAttachmentUpdated?: () => void;
	attachmentActionContext?: AttachmentMacroActionContext;
	attachmentActionExtensions?: ReadonlyArray<ForgeUIAttachmentActionExtensionType>;
};

type ExpandedRowDetailsProps = {
	attachment: AttachmentItem;
	contentId?: string;
	spaceKey?: string;
	dspBlocksDownloads: boolean;
	showPreview: boolean;
	showVersionHistory: boolean;
	onAttachmentUpdated?: () => void;
	attachmentActionContext?: AttachmentMacroActionContext;
	attachmentActionExtensions?: ReadonlyArray<ForgeUIAttachmentActionExtensionType>;
};

// Dialogs are inlined (not imported from attachments-page) to avoid a circular dep.

// Download URL helpers are in ../downloadUrl.ts (shared with VersionHistory).

// Lazily fetches version history when a row is expanded.
// Shows every version (even single-version files) to match legacy behaviour.
const ExpandedVersionHistory = ({
	attachment,
	contentId,
	dspBlocksDownloads,
}: {
	attachment: AttachmentItem;
	contentId?: string;
	dspBlocksDownloads: boolean;
}) => {
	const { versions } = useAttachmentVersions(attachment.id);

	if (versions.length < 1) {
		return null;
	}

	return (
		<VersionHistory
			attachmentId={attachment.id}
			versions={versions}
			contentId={contentId}
			fileName={attachment.fileName}
			dspBlocksDownloads={dspBlocksDownloads}
		/>
	);
};

const buildThumbnailUrl = (
	pageId: string | undefined,
	fileName: string,
	version?: number,
): string => {
	if (typeof window === 'undefined' || !pageId) {
		return '';
	}
	const versionParam = version ? `?version=${version}` : '';
	return `${window.location.origin}/wiki/download/thumbnails/${pageId}/${encodeURIComponent(
		fileName,
	)}${versionParam}`;
};

// Inline thumbnail preview. Falls back to a clickable card when the
// thumbnail 404s. Rendered as an anchor with legacy data attributes so
// the jQuery delegation opens the overlay on click.
const InlineAttachmentPreview = ({
	attachment,
	contentId,
	previewHref,
	bareAttachmentId,
	canPreview,
}: {
	attachment: AttachmentItem;
	contentId?: string;
	previewHref: string;
	bareAttachmentId: string;
	canPreview: boolean;
}) => {
	const intl = useIntl();
	const [thumbnailFailed, setThumbnailFailed] = useState(false);

	if (!attachment.hasViewPermission) {
		return null;
	}

	const thumbnailUrl = buildThumbnailUrl(contentId, attachment.fileName, attachment.version);
	if (!thumbnailUrl) {
		return null;
	}

	const altText = intl.formatMessage(i18n.inlinePreviewAlt, { fileName: attachment.fileName });

	return (
		// oxlint-disable-next-line @atlaskit/design-system/no-html-anchor
		<a
			data-testid={`attachments-preview-${attachment.id}`}
			href={canPreview ? previewHref : undefined}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- required for legacy jQuery selector matching
			className="previewAttachmentLink"
			data-linked-resource-type="attachment"
			data-linked-resource-id={bareAttachmentId}
			data-linked-resource-container-id={contentId}
			data-linked-resource-default-alias={attachment.fileName}
			data-linked-resource-content-type={attachment.mediaType ?? ''}
			css={styles.previewContainer}
		>
			{thumbnailFailed ? (
				<Box xcss={styles.previewFallback} testId={`attachments-preview-fallback-${attachment.id}`}>
					<Stack space="space.050">
						<Text weight="bold">{attachment.fileName}</Text>
						<Text color="color.text.subtle">
							<FormattedMessage {...i18n.preview} />
						</Text>
					</Stack>
				</Box>
			) : (
				<Box
					as="img"
					xcss={styles.previewImage}
					src={thumbnailUrl}
					alt={altText}
					loading="lazy"
					onError={() => setThumbnailFailed(true)}
				/>
			)}
		</a>
	);
};

const ExpandedRowDetails = ({
	attachment,
	contentId,
	spaceKey,
	dspBlocksDownloads,
	showPreview,
	showVersionHistory,
	onAttachmentUpdated,
	attachmentActionContext,
	attachmentActionExtensions = [],
}: ExpandedRowDetailsProps) => {
	const intl = useIntl();
	const editLabelsLabel = intl.formatMessage(i18n.editLabels);

	const { showModal } = useDialogs();
	// The confluence-previews WRM plugin is bootstrapped by the View Page shell,
	// so the delegation listener is always active — no local loader needed.
	// Guard: without a contentId the previewHref is '#' and the confluence-previews
	// delegation has no valid resource id to open, so skip the anchors entirely.
	const canPreview = Boolean(attachment.id && contentId);
	const bareAttachmentId = toPreviewAttachmentId(attachment.id);
	const previewHref = contentId
		? `/wiki/download/attachments/${contentId}/${encodeURIComponent(attachment.fileName)}`
		: '#';

	return (
		<Box xcss={styles.expandedDetailsWrapper} testId={`attachments-detail-${attachment.id}`}>
			<Stack space="space.150">
				{showPreview && (
					<InlineAttachmentPreview
						attachment={attachment}
						contentId={contentId}
						previewHref={previewHref}
						bareAttachmentId={bareAttachmentId}
						canPreview={canPreview}
					/>
				)}
				<Stack space="space.050">
					<Text weight="bold">
						<FormattedMessage {...i18n.labels} />
					</Text>
					<Box xcss={styles.labelsContentIndent}>
						<Inline space="space.100" alignBlock="center">
							{attachment.labels.length > 0 ? (
								<Inline space="space.050">
									{attachment.labels.map((label) => (
										<Tag
											key={label}
											text={label}
											href={buildLabelUrl(label, spaceKey)}
											isRemovable={false}
											color="grey"
										/>
									))}
								</Inline>
							) : (
								<Text color="color.text.subtle">
									<FormattedMessage {...i18n.noLabels} />
								</Text>
							)}
							{contentId ? (
								<IconButton
									appearance="subtle"
									spacing="compact"
									icon={EditIcon}
									label={editLabelsLabel}
									onClick={() => {
										showModal(EditLabelDialogLoader, {
											contentId: attachment.id,
											spaceKey: spaceKey ?? '',
											source: 'attachmentsMacro',
											onCloseComplete: onAttachmentUpdated,
										});
									}}
								/>
							) : (
								<IconButton
									appearance="subtle"
									spacing="compact"
									icon={EditIcon}
									label={editLabelsLabel}
									isDisabled
								/>
							)}
						</Inline>
					</Box>
				</Stack>

				{showVersionHistory && (
					<ExpandedVersionHistory
						key={`${attachment.id}-${attachment.version}-${attachment.versionComment}`}
						attachment={attachment}
						contentId={contentId}
						dspBlocksDownloads={dspBlocksDownloads}
					/>
				)}

				{/* Action buttons: Preview / View / Properties / Delete.
				    Preview and View are anchors with legacy data attributes — the
				    confluence-previews jQuery delegation opens the overlay on click. */}

				<Inline space="space.100">
					{canPreview &&
						attachment.hasPreviewPermission && (
							// oxlint-disable-next-line @atlaskit/design-system/no-html-anchor
							<a
								href={previewHref}
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- required for legacy jQuery selector matching
								className="previewAttachmentLink"
								data-linked-resource-type="attachment"
								data-linked-resource-id={bareAttachmentId}
								data-linked-resource-container-id={contentId}
								data-linked-resource-default-alias={attachment.fileName}
								data-linked-resource-content-type={attachment.mediaType ?? ''}
								css={styles.previewActionLink}
							>
								<FormattedMessage {...i18n.preview} />
							</a>
						)}
					{canPreview &&
						attachment.hasViewPermission && (
							// oxlint-disable-next-line @atlaskit/design-system/no-html-anchor
							<a
								href={previewHref}
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- required for legacy jQuery selector matching
								className="previewAttachmentLink"
								data-linked-resource-type="attachment"
								data-linked-resource-id={bareAttachmentId}
								data-linked-resource-container-id={contentId}
								data-linked-resource-default-alias={attachment.fileName}
								data-linked-resource-content-type={attachment.mediaType ?? ''}
								css={styles.previewActionLink}
							>
								<FormattedMessage {...i18n.view} />
							</a>
						)}
					{(attachment.hasEditPermission || attachment.hasDeletePermission) && contentId && (
						<>
							{attachment.hasEditPermission && (
								<Button
									appearance="default"
									spacing="compact"
									onClick={() => {
										showModal(AttachmentsMacroEditPropertiesDialogLoader, {
											contentId,
											attachment: {
												title: attachment.fileName,
												extensions: [
													{ key: 'mediaType', value: attachment.mediaType ?? '' },
													{ key: 'comment', value: attachment.versionComment ?? '' },
												],
											},
											attachmentId: attachment.id,
											labels: attachment.labels,
											spaceKey: spaceKey ?? '',
											handleAttachmentUpdated: onAttachmentUpdated ?? (() => {}),
										});
									}}
								>
									<FormattedMessage {...i18n.properties} />
								</Button>
							)}
							{attachment.hasDeletePermission && (
								<Button
									appearance="default"
									spacing="compact"
									onClick={() => {
										showModal(AttachmentsMacroDeleteDialogLoader, {
											attachmentId: attachment.id,
											attachmentName: attachment.fileName,
											onDeleted: onAttachmentUpdated,
										});
									}}
								>
									<FormattedMessage {...i18n.deleteAttachment} />
								</Button>
							)}
						</>
					)}
					<AttachmentWebItems attachmentId={attachment.id} slot="actions" />
					{attachmentActionContext && (
						<AttachmentMacroForgeActions
							attachmentId={attachment.id}
							context={attachmentActionContext}
							extensions={attachmentActionExtensions}
						/>
					)}
				</Inline>
			</Stack>
		</Box>
	);
};

export const AttachmentsTable = ({
	attachments,
	dspBlocksDownloads,
	showVersionHistory,
	showPreview,
	contentId,
	spaceKey,
	onAttachmentUpdated,
	attachmentActionContext,
	attachmentActionExtensions,
}: AttachmentsTableProps): React.JSX.Element => {
	const intl = useIntl();
	const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

	// Keep the clicked row pinned when expanding/collapsing (content grows downward).
	const expandAnchorRef = useRef<{ el: HTMLElement; top: number } | null>(null);

	useEffect(() => {
		const anchor = expandAnchorRef.current;
		if (!anchor) {
			return;
		}
		expandAnchorRef.current = null;
		const delta = anchor.el.getBoundingClientRect().top - anchor.top;
		if (delta === 0) {
			return;
		}
		let node: HTMLElement | null = anchor.el.parentElement;
		while (node) {
			const { overflowY } = window.getComputedStyle(node);
			if (
				(overflowY === 'auto' || overflowY === 'scroll') &&
				node.scrollHeight > node.clientHeight
			) {
				node.scrollTop += delta;
				return;
			}
			node = node.parentElement;
		}
		window.scrollBy(0, delta);
	}, [expandedIds]);

	const toggleExpand = (id: string, trigger: HTMLElement) => {
		expandAnchorRef.current = { el: trigger, top: trigger.getBoundingClientRect().top };
		setExpandedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	};

	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
		<div className="plugin_attachments_container">
			<Box as="table" xcss={styles.table} testId="attachments-table">
				<Box as="thead">
					<Box as="tr" xcss={styles.headerRow}>
						<Box as="th" xcss={styles.firstHeaderCell} {...{ scope: 'col' }}>
							<Text weight="bold" size="large">
								<FormattedMessage {...i18n.columnName} />
							</Text>
						</Box>
						<Box as="th" xcss={styles.modifiedHeaderCell} {...{ scope: 'col' }}>
							<Text weight="bold" size="large">
								<FormattedMessage {...i18n.columnModified} />
							</Text>
						</Box>
					</Box>
				</Box>
				<Box as="tbody">
					{attachments.flatMap((attachment) => {
						const isExpanded = expandedIds.has(attachment.id);

						const mainRow = (
							<Box as="tr" key={attachment.id} testId={`attachments-row-${attachment.id}`}>
								<Box as="td" xcss={styles.bodyCell}>
									<Inline space="space.100" alignBlock="center" shouldWrap>
										<Pressable
											type="button"
											aria-expanded={isExpanded}
											aria-label={intl.formatMessage(
												isExpanded ? i18n.collapseVersionHistory : i18n.expandVersionHistory,
												{ fileName: attachment.fileName },
											)}
											onClick={(event) => toggleExpand(attachment.id, event.currentTarget)}
											xcss={styles.expandButton}
											testId={`attachments-expand-${attachment.id}`}
										>
											{isExpanded ? (
												<ChevronDownIcon label="" size="small" />
											) : (
												<ChevronRightIcon label="" size="small" />
											)}
										</Pressable>
										<AttachmentIcon
											mediaType={attachment.mediaType}
											title={attachment.fileName}
											label=""
										/>
										<Stack>
											<Inline space="space.100" alignBlock="center" shouldWrap>
												{dspBlocksDownloads ? (
													<Text>{attachment.fileName}</Text>
												) : (
													<Anchor
														href={resolveDownloadUrl(
															contentId,
															attachment.fileName,
															attachment.version,
															attachment.downloadPath,
														)}
														xcss={styles.fileLink}
														testId={`attachments-link-${attachment.id}`}
													>
														{attachment.fileName}
													</Anchor>
												)}
												<AttachmentActionMetadata attachmentId={attachment.id} />
												<AttachmentWebItems attachmentId={attachment.id} slot="metadata" />
											</Inline>
											{attachment.versionComment ? <Text>{attachment.versionComment}</Text> : null}
										</Stack>
									</Inline>
								</Box>
								<Box as="td" xcss={styles.modifiedBodyCell}>
									<Text>
										<FormattedMessage
											{...i18n.dateByUser}
											values={{
												date: <SmartDate dateString={attachment.lastModificationDate} />,
												userName: attachment.lastModifier.accountId ? (
													<ProfileCardWrapper
														userId={attachment.lastModifier.accountId}
														fullName={attachment.lastModifier.displayName}
														position="bottom-start"
													>
														<ProfileLink
															userId={attachment.lastModifier.accountId}
															fullName={attachment.lastModifier.displayName}
															behavior="link"
															referralSource="attachments-macro"
															isUnderlinedByDefault={false}
														/>
													</ProfileCardWrapper>
												) : (
													attachment.lastModifier.displayName
												),
											}}
										/>
									</Text>
								</Box>
							</Box>
						);

						if (!isExpanded) {
							return [mainRow];
						}

						const expandedRow = (
							<Box
								as="tr"
								key={`${attachment.id}-expanded`}
								testId={`attachments-row-${attachment.id}-expanded`}
							>
								<Box as="td" xcss={styles.expandedCell} {...{ colSpan: 2 }}>
									<ExpandedRowDetails
										attachment={attachment}
										contentId={contentId}
										spaceKey={spaceKey}
										dspBlocksDownloads={dspBlocksDownloads}
										showPreview={showPreview}
										showVersionHistory={showVersionHistory}
										onAttachmentUpdated={onAttachmentUpdated}
										attachmentActionContext={attachmentActionContext}
										attachmentActionExtensions={attachmentActionExtensions}
									/>
								</Box>
							</Box>
						);

						return [mainRow, expandedRow];
					})}
				</Box>
			</Box>
		</div>
	);
};
